import {
	createVariantChangeImmediateEvent,
	PUB_SUB_EVENTS,
} from '@/scripts/core/global'
import { createVariantChangeEvent, publish } from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type ProductVariant } from '@/scripts/types/api'
import { type ProductForm } from '@/scripts/product/product-form'
import { type ShareButton } from '@/scripts/optional/share-button'
import { type PickupAvailability } from '@/scripts/optional/pickup-availability'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type ProductStickyCta } from '@/scripts/product/product-sticky-cta'
import { type ProductInfo } from '@/scripts/product/product-info'
import { type MediaGallery } from '@/scripts/product/media-gallery'

type VariantMedia = {
	variantId: string
	variantMedia: string
	quantity: number
	productTitle: string
}

export class VariantSelects extends UcoastEl {
	static htmlSelector = 'variant-selects'
	static selectors = {
		sliderContainer: '',
		sliderTrack: '',
		sliderLabels: '',
		sliderInputs: '',
		sliderNextbutton: '',
		sliderPrevbutton: '',
		closeModalButton: '',
	}
	instanceSelectors = VariantSelects.selectors
	currentVariant?: ProductVariant
	options?: string[]
	variantData?: ProductVariant[]
	isQuickAdd: boolean
	source: string
	productId: string
	parentComponent?: ProductInfo | ProductStickyCta
	externalSource: string | undefined
	variantMedia?: VariantMedia[]
	featuredMediaEls?: NodeListOf<HTMLImageElement>
	formId: string
	quantityInput: HTMLInputElement
	colorText?: HTMLElement
	mediaGallery?: MediaGallery

	constructor() {
		super()
		this.setInstanceSelectors()
		this.addEventListener('change', this.onVariantChange.bind(this))
		this.isQuickAdd = false
		this.source = q.ra(this, 'data-source')
		this.productId = q.ra(this, 'data-uc-product-id')
		this.parentComponent = this.getParentComponent()
		this.featuredMediaEls = q.ol(
			`[data-uc-featured-media="${this.source}"]`
		)
		this.variantMedia = this.getVariantMedia()
		this.formId = q.ra(this, 'data-uc-form-id')
		this.quantityInput = q.rs<HTMLInputElement>(
			`[data-uc-quantity-input][form="${this.formId}"]`
		)
		this.colorText = q.os<HTMLElement>('[data-uc-variant-color-text]', this)
		this.mediaGallery = q.os<MediaGallery>(
			`media-gallery[data-product-id="${this.productId}"]`
		)

		this.onVariantChange(new Event('change'))
	}

	getParentComponent() {
		if (this.source !== 'sticky' && this.source !== 'primary')
			return undefined
		return this.source === 'sticky'
			? q.os<ProductStickyCta>(
					`product-sticky-cta[data-uc-product-id="${this.productId}"]`
				)
			: q.os<ProductInfo>(
					`product-info[data-uc-product-id="${this.productId}"]`
				)
	}

	setInstanceSelectors() {
		this.instanceSelectors = VariantSelects.selectors
	}

	onVariantChange(_: Event, fromExternalSource = false) {
		console.log('onVariantChange')
		this.updateOptions()
		this.updateMasterId()
		this.toggleAddButton(true, '', false)
		this.updatePickupAvailability()
		this.removeErrorMessage()
		this.updateVariantStatuses()
		this.updateVariantMediaVisibilityVariables()

		if (!this.currentVariant) {
			this.toggleAddButton(true, '', true)
			this.setUnavailable()
		} else {
			this.updateURL()
			this.updateVariantInput()
			this.renderProductInfo()
			this.updateShareUrl()
		}
		if (this.parentComponent && !fromExternalSource) {
			if (this.currentVariant?.id) {
				publish(
					PUB_SUB_EVENTS.variantChangeImmediate,
					createVariantChangeImmediateEvent({
						source: 'variant-change-immediate',
						data: {
							variantId: this.currentVariant.id,
						},
					})
				)
			}
			this.parentComponent.updateSiblingForm()
		}
	}

	updateOptions() {
		this.options = Array.from(
			q.rl<HTMLSelectElement>('select', this),
			(select) => select.value
		)
	}

	updateMasterId() {
		this.currentVariant = this.getVariantData().find((variant) => {
			return !variant.options
				.map((option, index) => {
					return this.options && this.options[index] === option
				})
				.includes(false)
		})
	}

	updateURL() {
		if (!this.currentVariant || this.dataset.updateUrl === 'false') return
		window.history.replaceState(
			{},
			'',
			`${this.dataset.url}?variant=${this.currentVariant.id}`
		)
	}

	updateShareUrl() {
		if (!this.currentVariant)
			throw new Error('cannot updateShareUrl without currentVariant')
		const shareButton = q.os<ShareButton>(`#Share-${this.dataset.section}`)
		if (!shareButton || !shareButton.updateUrl) return
		shareButton.updateUrl(
			`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`
		)
	}

	updateVariantInput() {
		const currentVariant = this.currentVariant
		if (!currentVariant)
			throw new Error('cannot updateVariantInput without currentVariant')
		const productForms = q.ol(
			`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
		)
		productForms?.forEach((productForm) => {
			const input = q.rs<HTMLInputElement>(
				'input[name="id"]',
				productForm
			)
			input.value = `${currentVariant.id}`
			input.dispatchEvent(new Event('change', { bubbles: true }))
		})
	}

	getVariantOptionFromIndex(variant: ProductVariant, index: number) {
		switch (index) {
			case 1:
				return variant.option1
			case 2:
				return variant.option2
			case 3:
				return variant.option3
			default:
				throw new Error('invalid index')
		}
	}

	updateVariantStatuses() {
		if (!this.variantData)
			throw new Error('cannot updateVariantStatuses without variantData')
		const selectedOptionOneVariants = this.variantData.filter(
			(variant) =>
				q.rs<HTMLInputElement | HTMLOptionElement>(':checked', this)
					.value === variant.option1
		)
		const inputWrapperNodes = q.ol('.product-form__input', this)
		const inputWrappers = inputWrapperNodes?.length
			? Array.from(inputWrapperNodes)
			: []
		if (inputWrappers.length) {
			inputWrappers.forEach((option: HTMLElement, index: number) => {
				if (index === 0) return
				const optionInputNodes = q.ol<
					HTMLInputElement | HTMLOptionElement
				>('input[type="radio"], option', option)
				const optionInputs = optionInputNodes?.length
					? Array.from(optionInputNodes)
					: []

				const previousOptionSelected = q.rs<
					HTMLInputElement | HTMLOptionElement
				>(':checked', inputWrappers[index - 1]).value
				const availableOptionInputsValue = selectedOptionOneVariants
					.filter(
						(variant) =>
							variant.available &&
							this.getVariantOptionFromIndex(variant, index) ===
								previousOptionSelected
					)
					.map((variantOption) =>
						this.getVariantOptionFromIndex(variantOption, index + 1)
					)
				this.setInputAvailability(
					optionInputs,
					availableOptionInputsValue
				)
			})
		} else {
			console.warn('no input wrappers found')
		}
	}

	setInputAvailability(
		listOfOptions: (HTMLInputElement | HTMLOptionElement)[],
		listOfAvailableOptions: (string | null)[]
	) {
		listOfOptions.forEach((input) => {
			if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
				input.innerText = q.ra(input, 'value')
			} else {
				input.innerText =
					window.variantStrings.unavailable_with_option.replace(
						'[value]',
						q.ra(input, 'value')
					)
			}
		})
	}

	updatePickupAvailability() {
		const pickUpAvailability = q.os<PickupAvailability>(
			'pickup-availability'
		)
		if (!pickUpAvailability) return
		const currentVariant = this.currentVariant

		if (currentVariant && currentVariant.available) {
			pickUpAvailability.fetchAvailability(`${currentVariant.id}`)
		} else {
			pickUpAvailability.removeAttribute('available')
			pickUpAvailability.innerHTML = ''
		}
	}

	removeErrorMessage() {
		const section = this.closest('section')
		if (!section) return

		const productForm = q.os<ProductForm>('product-form', section)
		if (productForm) productForm.handleErrorMessage()
	}

	renderProductInfo() {
		if (!this.currentVariant)
			throw new Error(
				'this.currentVariant not selected, renderProductInfo called too early'
			)
		const requestedVariantId = this.currentVariant.id
		const currentQuantity =
			this?.quantityInput && this.source === 'quick-add'
				? parseInt(this.quantityInput.value)
				: 1
		const sectionId = this.dataset.originalSection
			? this.dataset.originalSection
			: q.ra(this, 'data-section')

	/*	if (this.mediaGallery) {
			this.mediaGallery.switchVariant(requestedVariantId)
		}*/

		fetch(
			`${this.dataset.url}?variant=${requestedVariantId}&section_id=${
				this.dataset.originalSection
					? this.dataset.originalSection
					: this.dataset.section
			}`
		)
			.then((response) => response.text())
			.then((responseText) => {
				if (!this.currentVariant)
					throw new Error(
						'this.currentVariant not selected, renderProductInfo called too early'
					)
				// prevent unnecessary ui changes from abandoned selections
				if (this.currentVariant.id !== requestedVariantId) return

				const html = new DOMParser().parseFromString(
					responseText,
					'text/html'
				)
				const destination = document.getElementById(
					`price-${this.dataset.section}`
				)
				const source = html.getElementById(
					`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
				)
				const skuSource = html.getElementById(
					`Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
				)
				const skuDestination = document.getElementById(
					`Sku-${this.dataset.section}`
				)
				const inventorySource = q.os(
					`#Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`,
					html
				)
				const inventoryDestination = document.getElementById(
					`Inventory-${this.dataset.section}`
				)

				const colorTextSource = q.os(
					'[data-uc-variant-color-text]',
					html
				)
				const colorTextDestination = q.os(
					'[data-uc-variant-color-text]',
					this
				)

				if (source && destination)
					destination.innerHTML = source.innerHTML
				if (inventorySource && inventoryDestination)
					inventoryDestination.innerHTML = inventorySource.innerHTML
				if (skuSource && skuDestination) {
					skuDestination.innerHTML = skuSource.innerHTML
					skuDestination.classList.toggle(
						'visibility-hidden',
						skuSource.classList.contains('visibility-hidden')
					)
				}

				const price = document.getElementById(
					`price-${this.dataset.section}`
				)

				if (price) price.classList.remove('visibility-hidden')

				if (inventoryDestination)
					inventoryDestination.classList.toggle(
						'visibility-hidden',
						inventorySource?.innerText === ''
					)


				if (colorTextSource && colorTextDestination) {
					colorTextDestination.innerHTML = colorTextSource.innerHTML
				} else if (colorTextDestination) {
					colorTextDestination.innerHTML = ''
				}

				const addButtonUpdated = html.getElementById(
					`ProductSubmitButton-${sectionId}`
				)
				this.toggleAddButton(
					addButtonUpdated
						? addButtonUpdated.hasAttribute('disabled')
						: true,
					window.variantStrings.soldOut
				)

				publish(
					PUB_SUB_EVENTS.variantChange,
					createVariantChangeEvent({
						source: 'product-form',
						data: {
							sectionId,
							html,
							variant: this.currentVariant,
							currentQuantity,
						},
					})
				)
			})
	}

	toggleAddButton(_disable = true, text: string, modifyClass = true) {
		const productForm = q.rs<HTMLFormElement>(
			`#product-form-${this.dataset.section}`
		)
		if (!productForm) return
		const addButton = q.os('[name="add"]', productForm)
		const addButtonText = q.os('[name="add"] > span', productForm)
		if (!addButton || !addButtonText) return

		if (this.currentVariant?.inventory_status === 'ComingSoon') {
			addButton.removeAttribute('disabled')
			addButton.classList.remove('visually-disabled')
			addButtonText.textContent =
				this.currentVariant.inventory_status_text
			addButton.setAttribute(
				'data-uc-klaviyo-oos-popup-trigger',
				`${this.currentVariant.id}`
			)
			this.updatePreorderText('')
		} else if (this.currentVariant?.inventory_status === 'OutOfStock') {
			addButton.setAttribute('disabled', 'disabled')
			addButton.removeAttribute('data-uc-klaviyo-oos-popup-trigger')
			if (text) addButtonText.textContent = text
			this.updatePreorderText('')
			addButton.classList.add('visually-disabled')
		} else if (
			this.currentVariant?.inventory_status === 'PreOrder' ||
			this.currentVariant?.variant_preorder_text
		) {
			addButton.removeAttribute('disabled')
			addButtonText.textContent =
				this.currentVariant.inventory_status_text
			addButton.removeAttribute('data-uc-klaviyo-oos-popup-trigger')
			this.updatePreorderText(
				this.currentVariant.variant_preorder_text ||
					this.currentVariant.preorder_text
			)
			addButton.classList.remove('visually-disabled')
		} else {
			addButton.removeAttribute('disabled')
			addButtonText.textContent = window.variantStrings.addToCart
			addButton.removeAttribute('data-uc-klaviyo-oos-popup-trigger')
			this.updatePreorderText('')
			addButton.classList.remove('visually-disabled')
		}

		if (!modifyClass) return
	}
	updatePreorderText(newContent: string) {
		const preorderTextNodes = q.ol('[data-uc-preorder-text]')
		preorderTextNodes?.forEach((el) => {
			el.innerHTML = newContent
		})
	}
	updateVariantMediaVisibilityVariables() {
		const currentVariantId = this.currentVariant?.id
		const root = q.rs('html')

		this.variantData?.forEach((variant) => {
			root.style.setProperty(
				`--variant-media-visibility-${variant.id}`,
				variant.id === currentVariantId ? 'initial' : 'none'
			)
		})
	}

	setUnavailable() {
		const button = q.rs(`#product-form-${this.dataset.section}`)
		const addButton = q.os('[name="add"]', button)
		const addButtonText = q.os('[name="add"] > span', button)
		const price = document.getElementById(`price-${this.dataset.section}`)
		const inventory = document.getElementById(
			`Inventory-${this.dataset.section}`
		)
		const sku = document.getElementById(`Sku-${this.dataset.section}`)

		if (!addButton || !addButtonText) return
		addButtonText.textContent = window.variantStrings.unavailable
		if (price) price.classList.add('visibility-hidden')
		if (inventory) inventory.classList.add('visibility-hidden')
		if (sku) sku.classList.add('visibility-hidden')
	}

	replaceVariantMedia() {
		this.updateOptions()
		this.updateMasterId()
		if (!this.variantMedia) return
		const sourceMediaWidth = q
			.os(`[data-uc-featured-media="${this.source}"] .product__media`)
			?.getBoundingClientRect().width
		if (!sourceMediaWidth) {
			console.log('source media not found, not replacing media', this)
			return
		}
		const currentQuantity =
			this?.quantityInput && this.source === 'quick-add'
				? parseInt(this.quantityInput.value)
				: 1
		let filteredMedia: VariantMedia[] | null = this.variantMedia.filter(
			(media) => {
				return (
					media.variantId === `${this.currentVariant?.id}` &&
					media.quantity <= currentQuantity
				)
			}
		)
		if (filteredMedia.length === 0) return

		let newMedia = filteredMedia[filteredMedia.length - 1]

		// force clear memory
		filteredMedia = null
		const retinaSize = (sourceMediaWidth * 2).toFixed(0)
		const newMediaUrl = newMedia.variantMedia
			.replace('_22x', ``)
			.replace('.jpg', `.jpg?width=${retinaSize}`)
		const imagesToSwap = [
			q.os(
				`[data-uc-featured-media="${this.source}"] .product__media img`
			),
			q.os(
				`[data-uc-featured-media="${this.source}"].product__media-item__wrapper img`
			),
		]

		imagesToSwap.forEach((imageToSwap) => {
			if (!imageToSwap) {
				return
			}
			imageToSwap.setAttribute('src', newMediaUrl)
		})
		const title = q.os(
			`[data-uc-dynamic="product-title"][data-source="${this.source}"]`
		)
		if (title) {
			title.innerHTML = newMedia.productTitle
		}
	}

	getVariantMedia(): VariantMedia[] | undefined {
		let textContent = q.rs<HTMLScriptElement>(
			`[type="application/json"][data-uc-product-media-source="${this.source}"]`
		).textContent
		if (!textContent) {
			console.error('no textcontent')
			return undefined
		}
		const media = JSON.parse(textContent)?.media
		if (!media?.length) {
			console.error('no variant media found', this)
			return undefined
		}
		for (let i in media) {
			const mediaItem = media[i]
			if (
				!(
					typeof mediaItem.quantity === 'number' &&
					typeof mediaItem?.variantId === 'string' &&
					typeof mediaItem.variantMedia === 'string'
				)
			) {
				console.error('invalid media items, disable media swap', this)
				return undefined
			}
		}
		return media
	}

	getVariantData(): ProductVariant[] {
		const textContent = q.rs<HTMLScriptElement>(
			'[type="application/json"][data-uc-variant-data]',
			this
		).textContent
		if (!textContent) throw new Error('Variant data not found')
		const updatedVariantData = this.variantData ?? JSON.parse(textContent)
		this.variantData = updatedVariantData
		return updatedVariantData
	}
}
